/**
 * Popular destinations shown on the 404 page.
 * Ordered by business value: free entry point first, then paid offers,
 * then proof/authority pages. Keep this short — 6 to 8 links converts best.
 */
import { ROUTES } from "@/content/offers";

export type PopularLink = {
  to: string;
  label: string;
  blurb: string;
};

export const POPULAR_LINKS: PopularLink[] = [
  {
    to: ROUTES.scan,
    label: "Free Make Change Scan",
    blurb: "Two minutes. Pinpoints the one bottleneck holding your next level back.",
  },
  {
    to: ROUTES.masterclass,
    label: "Built On Purpose Masterclass",
    blurb: "The live intensive that turns calling into a working operating system.",
  },
  {
    to: "/ebooks",
    label: "Resources & eBooks",
    blurb: "Every downloadable guide, workbook, and playbook in one library.",
  },
  {
    to: "/books",
    label: "Books",
    blurb: "Print titles from Dr. Sully — PsyD · ThD · DMin · CERC.",
  },
  {
    to: ROUTES.advocacy,
    label: "Full-Scope Advocacy",
    blurb: "Faith-integrated, trauma-informed advocacy partnership.",
  },
  {
    to: "/speaking",
    label: "Speaking",
    blurb: "Keynotes and trainings for churches, agencies, and conferences.",
  },
  {
    to: "/blog",
    label: "Blog",
    blurb: "Essays on healing, leadership, and building on purpose.",
  },
  {
    to: ROUTES.inquire,
    label: "Work With Dr. Sully",
    blurb: "Start a conversation about coaching, consulting, or advocacy.",
  },
];

/** Words that hint at what a visitor was probably looking for. */
const INTENT_HINTS: Array<{ match: RegExp; to: string; label: string }> = [
  { match: /book|read|pdf|ebook|download|guide|workbook|resource|librar/i, to: "/ebooks", label: "Resources & eBooks" },
  { match: /speak|keynote|event|conference|stage|host/i, to: "/speaking", label: "Speaking" },
  { match: /coach|consult|hire|work|contact|inquir|coaching/i, to: ROUTES.inquire, label: "Work With Dr. Sully" },
  { match: /advoca|fsap|case|support|client/i, to: ROUTES.advocacy, label: "Full-Scope Advocacy" },
  { match: /class|webinar|training|course|cert|masterclass/i, to: ROUTES.masterclass, label: "Built On Purpose Masterclass" },
  { match: /price|cost|pricing|invest|pay|checkout|buy/i, to: ROUTES.invest, label: "Pricing & Investment" },
  { match: /story|stories|testimon|review|proof/i, to: "/reviews", label: "Reviews & Stories" },
  { match: /blog|post|article|news/i, to: "/blog", label: "Blog" },
  { match: /scan|assess|quiz|framework/i, to: ROUTES.scan, label: "Free Make Change Scan" },
];

/**
 * Best-guess destination for a broken path, e.g. /old-speaking-page -> /speaking.
 * Returns null when nothing in the path is recognizable.
 */
export function suggestDestination(pathname: string): { to: string; label: string } | null {
  const hint = INTENT_HINTS.find((h) => h.match.test(pathname));
  return hint ? { to: hint.to, label: hint.label } : null;
}
